import { useCallback, useEffect, useRef, useState } from "react";

// Noise component integrated into the background
function Noise({
	patternSize = 100,
	patternScaleX = 1, // How much to scale the noise pattern tiling horizontally
	patternScaleY = 1, // How much to scale the noise pattern tiling vertically
	patternRefreshInterval = 1,
	patternAlpha = 50,
	intensity = 1,
	onReady = () => {},
}) {
	const grainRef = useRef(null);
	// Ref to store the CSS dimensions of the canvas to avoid repeated getBoundingClientRect in loop
	const canvasCssSizeRef = useRef({ width: 0, height: 0 });
	// DPR the current bitmap was allocated for; a monitor change invalidates it
	const canvasDprRef = useRef(0);

	useEffect(() => {
		const canvas = grainRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) {
			console.error("Failed to get 2D context for noise canvas.");
			return;
		}

		let frame = 0;
		const patternCanvas = document.createElement("canvas");
		patternCanvas.width = patternSize;
		patternCanvas.height = patternSize;

		const patternCtx = patternCanvas.getContext("2d");
		if (!patternCtx) {
			console.error("Failed to get 2D context for pattern sub-canvas.");
			return;
		}
		const patternData = patternCtx.createImageData(patternSize, patternSize);
		const patternPixelDataLength = patternSize * patternSize * 4; // 4 for R, G, B, A

		// Size the drawing surface once, large enough to cover any viewport this
		// window can reach. Resizing the bitmap on every resize event clears the
		// canvas and makes the grain flicker, so we only ever grow it.
		const resize = () => {
			const dpr = window.devicePixelRatio || 1;
			const newCssWidth = Math.max(
				window.innerWidth,
				window.screen?.width || 0,
			);
			const newCssHeight = Math.max(
				window.innerHeight,
				window.screen?.height || 0,
			);

			// Dragging the window to a monitor with a different pixel ratio keeps the
			// CSS size but invalidates the bitmap, so re-allocate on a DPR change too.
			if (
				dpr === canvasDprRef.current &&
				newCssWidth <= canvasCssSizeRef.current.width &&
				newCssHeight <= canvasCssSizeRef.current.height
			) {
				return;
			}

			// Keep whatever surface we already grew to; only ever expand it.
			const cssWidth = Math.max(newCssWidth, canvasCssSizeRef.current.width);
			const cssHeight = Math.max(newCssHeight, canvasCssSizeRef.current.height);

			canvasCssSizeRef.current = { width: cssWidth, height: cssHeight };
			canvasDprRef.current = dpr;

			canvas.width = cssWidth * dpr;
			canvas.height = cssHeight * dpr;
			canvas.style.width = `${cssWidth}px`;
			canvas.style.height = `${cssHeight}px`;

			// Set the transformation matrix to account for DPR.
			// This allows drawing commands to use CSS pixel units.
			// Subsequent ctx.scale for pattern will be relative to this.
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};

		const updatePattern = () => {
			for (let i = 0; i < patternPixelDataLength; i += 4) {
				const value = Math.random() * 255 * intensity;
				patternData.data[i] = value;
				patternData.data[i + 1] = value;
				patternData.data[i + 2] = value;
				patternData.data[i + 3] = patternAlpha;
			}
			patternCtx.putImageData(patternData, 0, 0);
		};

		const drawGrain = () => {
			const { width: cssWidth, height: cssHeight } = canvasCssSizeRef.current;
			if (cssWidth === 0 || cssHeight === 0) return; // Don't draw if canvas has no dimensions

			// Clear using CSS pixel dimensions (context is already scaled by DPR)
			ctx.clearRect(0, 0, cssWidth, cssHeight);

			ctx.save();

			// Scale the context for tiling the pattern.
			// This affects how the pattern is repeated.
			// Using Math.max to prevent division by zero if scale is 0.
			const safePatternScaleX = Math.max(0.001, patternScaleX);
			const safePatternScaleY = Math.max(0.001, patternScaleY);
			ctx.scale(safePatternScaleX, safePatternScaleY);

			const fillPattern = ctx.createPattern(patternCanvas, "repeat");
			if (fillPattern) {
				ctx.fillStyle = fillPattern;
				// Fill a rectangle that, in the *scaled* coordinate system,
				// corresponds to the original cssWidth/cssHeight.
				ctx.fillRect(
					0,
					0,
					cssWidth / safePatternScaleX,
					cssHeight / safePatternScaleY,
				);
			}

			ctx.restore();
		};

		let animationFrameId = 0;
		const loop = () => {
			// Only update and draw if dimensions are valid
			if (
				canvasCssSizeRef.current.width > 0 &&
				canvasCssSizeRef.current.height > 0
			) {
				if (frame % patternRefreshInterval === 0) {
					updatePattern();
					drawGrain();
				}
			}
			frame++;
			animationFrameId = window.requestAnimationFrame(loop);
		};

		// A monitor change may not fire 'resize' if the CSS size is unchanged, so
		// watch the pixel ratio directly as well.
		let dprQuery: MediaQueryList | null = null;
		const watchDpr = () => {
			dprQuery?.removeEventListener("change", onDprChange);
			dprQuery = window.matchMedia(
				`(resolution: ${window.devicePixelRatio}dppx)`,
			);
			dprQuery.addEventListener("change", onDprChange);
		};
		const onDprChange = () => {
			resize();
			drawGrain();
			watchDpr();
		};

		window.addEventListener("resize", resize);
		watchDpr();
		resize(); // Initial setup: size canvas and draw first frame if needed
		// Paint one frame synchronously so the canvas is never blank, then hand
		// over from the server-rendered static grain.
		updatePattern();
		drawGrain();
		onReady();
		if (patternRefreshInterval > 0) {
			// Start loop only if refresh is meaningful
			loop();
		}

		return () => {
			window.removeEventListener("resize", resize);
			dprQuery?.removeEventListener("change", onDprChange);
			if (animationFrameId) {
				window.cancelAnimationFrame(animationFrameId);
			}
		};
	}, [
		patternSize,
		patternScaleX,
		patternScaleY,
		patternRefreshInterval,
		patternAlpha,
		intensity,
		onReady,
	]);

	return (
		<canvas className="fixed top-0 left-0 pointer-events-none" ref={grainRef} />
	);
}

// SSR-rendered grain. An SVG turbulence filter needs no JS, so it is painted on
// the very first frame and covers the gap before the canvas takes over.
function StaticGrain({ alpha = 50, patternSize = 100 }) {
	const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${patternSize}' height='${patternSize}'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='1' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(#n)'/></svg>`;

	return (
		<div
			aria-hidden
			className="fixed inset-0 pointer-events-none"
			style={{
				backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
				backgroundSize: `${patternSize}px ${patternSize}px`,
				opacity: alpha / 255,
			}}
		/>
	);
}

// Main gradient background component
function GradientBackground({
	// Gradient customization
	gradientType = "radial-gradient",
	gradientSize = "125% 125%",
	gradientOrigin = "bottom-middle",
	colors = [
		{ color: "rgba(245,87,2,1)", stop: "10.5%" },
		{ color: "rgba(245,120,2,1)", stop: "16%" },
		{ color: "rgba(245,140,2,1)", stop: "17.5%" },
		{ color: "rgba(245,170,100,1)", stop: "25%" },
		{ color: "rgba(238,174,202,1)", stop: "40%" },
		{ color: "rgba(202,179,214,1)", stop: "65%" },
		{ color: "rgba(148,201,233,1)", stop: "100%" },
	],

	// Noise customization
	enableNoise = true,
	noisePatternSize = 100,
	noisePatternScaleX = 1,
	noisePatternScaleY = 1,
	noisePatternRefreshInterval = 1,
	noisePatternAlpha = 50,
	noiseIntensity = 1,

	// Additional styling
	className = "",
	style = {},
	children,

	// Custom gradient string (overrides other gradient props if provided)
	customGradient = null,
}) {
	// Generate gradient string from colors array
	const generateGradient = () => {
		if (customGradient) return customGradient;

		const getGradientPosition = (origin) => {
			const positions = {
				"bottom-middle": "50% 101%",
				"bottom-left": "0% 101%",
				"bottom-right": "100% 101%",
				"top-middle": "50% -1%",
				"top-left": "0% -1%",
				"top-right": "100% -1%",
				"left-middle": "-1% 50%",
				"right-middle": "101% 50%",
				center: "50% 50%",
			};
			return positions[origin] || positions["bottom-middle"];
		};

		const position = getGradientPosition(gradientOrigin);
		const colorStops = colors
			.map(({ color, stop }) => `${color} ${stop}`)
			.join(",");

		if (gradientType === "radial-gradient") {
			return `radial-gradient(${gradientSize} at ${position},${colorStops})`;
		} else if (gradientType === "linear-gradient") {
			const angleMap = {
				"bottom-middle": "0deg", // to top
				"bottom-left": "45deg", // to top-right
				"bottom-right": "315deg", // to top-left
				"top-middle": "180deg", // to bottom
				"top-left": "135deg", // to bottom-right
				"top-right": "225deg", // to bottom-left
				"left-middle": "90deg", // to right
				"right-middle": "270deg", // to left
				center: "0deg", // Default for center, though linear usually implies direction
			};
			const angle = angleMap[gradientOrigin] || angleMap["bottom-middle"];
			return `linear-gradient(${angle},${colorStops})`;
		} else if (gradientType === "conic-gradient") {
			// Conic gradients usually need a 'from <angle>' and 'at <position>'
			// For simplicity, we'll use the position and a default 'from' angle.
			return `conic-gradient(from 0deg at ${position},${colorStops})`;
		}

		// Fallback for unknown gradient types or simple color stop list
		return `${gradientType}(${colorStops})`;
	};

	const [noiseReady, setNoiseReady] = useState(false);
	const handleNoiseReady = useCallback(() => setNoiseReady(true), []);

	const gradientStyle = {
		background: generateGradient(),
		...style,
	};

	return (
		<div className={`relative min-h-screen w-full ${className}`}>
			<div
				className="fixed inset-0 w-screen h-screen -z-10"
				style={gradientStyle}
			/>
			{enableNoise && !noiseReady && (
				<StaticGrain alpha={noisePatternAlpha} patternSize={noisePatternSize} />
			)}
			{enableNoise && (
				<Noise
					onReady={handleNoiseReady}
					patternSize={noisePatternSize}
					patternScaleX={noisePatternScaleX}
					patternScaleY={noisePatternScaleY}
					patternRefreshInterval={noisePatternRefreshInterval}
					patternAlpha={noisePatternAlpha}
					intensity={noiseIntensity}
				/>
			)}
			{children}
		</div>
	);
}

export { GradientBackground };
